import React, { useContext, useState, useRef, useEffect } from "react";
import { GraphContext } from "../../../context/GraphContext";
import "./loop.scss";
import Chi2LoopEntry from "./Chi2LoopEntry";
// import { Button, TextField } from "@mui/material";
import { TextField } from "@mui/material";
import BlockingOverlay from "../../commonComponents/BlockingOverlay";
import { deepCopy, getModelById } from "../../leftSide/Models/modelLogic";
import { DashboardContext } from "../../../context/DashboardContext";
import { WebSocketContext } from "../../../context/WebSocketContext";
import { AuthContext } from "../../../context/AuthContext";
import { GeneralContext } from "../../../context/GeneralContext";
import {
  filterCurveDataToRange,
  generateModelsRangesQuantPairsForModelDist,
  getAllIdsForModelsAndSubmodels,
  getRangesForModelsFromGraphs,
  hasProperty,
  segmentsIntoSingleCurve,
  splitCurveIntoSegmentsByEdges,
  getQuantityNameFromModels,
  isInverseUnit,
  convertRangeRequestFromUnitsToCm1,
  jsxToHtmlString,
  createDataPayloadForModels,
  generateWarningObject,
  getQuantityXUnitFromModels,
} from "../../../utils/helpers";
import {
  containedModelIDsInGraphs,
  createModelDistPayload,
  createModelFitPayloadParams,
} from "../graphLogic";
import units from "../../../utils/units";
import Modal from "react-modal";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import ParameterSelectorWindow from "./ParameterSelectorWindow";
import TrackedParamDisplay from "./TrackedParamDisplay";
import CustomButton from "../../commonComponents/CustomButton";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";
import ExportCurvesModal from "../WindowComponents/ExportCurvesModal";

function Loop() {
  const { chi2Terms, setChi2Terms, graphs, selectedChi2Terms } =
    useContext(GraphContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const {
    modelData,
    //setModelData,
    chiVal,
    maxRunTime,
    iterationRefreshCount,
    maxIteration,
    uploadedFiles,
    loopModelFilesCustomName,
    setLoopModelFilesCustomName,
    listOfLoopModelsToSave,
    setListOfLoopModelsToSave,
    listOfLoopCurvesToSave,
    setListOfLoopCurvesToSave,
    setLoopOpen,
    setOutputsOpen,
    setParametersOpen,
    trackedParameters,
    setTrackedParameters,
    setPauseOnNextLoop,
    setWarnings,
    setNewWarningCount,
    limitedToast,
  } = useContext(DashboardContext);
  const { sendJsonMessage, setIsLoopOngoing, isFitOngoing, isLoopOngoing } =
    useContext(WebSocketContext);
  const { currentUser, checkAccessLevel } = useContext(AuthContext);
  const [paramSelectOpen, setParamSelectOpen] = useState(false);
  const [autosaveModSelectOpen, setAutosaveModSelectOpen] = useState(false);
  const [exportCurvesModalOpen, setExportCurvesModalOpen] = useState(false);
  const [modelsForCurvesDictionary, setModelsForCurvesDictionary] = useState(
    []
  );
  const paramSelectRef = useRef();
  const autosaveModSelectRef = useRef();
  const autosaveCurveSelectRef = useRef();

  useEffect(() => {
    try {
      const modelsConnected = [];

      const idsAlreadyIn = [];

      for (let i = 0; i < chi2Terms.length; i++) {
        const term = chi2Terms[i];

        if (hasProperty(term, "modelList")) {
          for (let j = 0; j < term.modelList.length; j++) {
            const modelEntry = term.modelList[j];

            if (!idsAlreadyIn.some((id) => id === modelEntry.FE_ID)) {
              let currentEntry = listOfLoopModelsToSave.find(
                (currentEntry) => currentEntry.FE_ID === modelEntry.FE_ID
              );

              if (currentEntry === undefined) {
                currentEntry = { ...modelEntry, save: false };
              }

              modelsConnected.push(currentEntry);
              idsAlreadyIn.push(modelEntry.FE_ID);
            }
          }
        }
      }

      setListOfLoopModelsToSave(modelsConnected);
    } catch (error) {
      recordedErrorLog("Error in batch model list production: ", error);
    }
  }, [chi2Terms]);

  useEffect(() => {
    try {
      if (graphs !== undefined && listOfLoopModelsToSave !== undefined) {
        // Extract model IDs from listOfLoopModelsToSave (which is already built from chi2Terms)
        const listOfModelIds = listOfLoopModelsToSave.map(
          (entry) => entry.FE_ID
        );

        // Expand to include all submodels
        const expandedModelIds = getAllIdsForModelsAndSubmodels(
          listOfModelIds,
          modelData
        );

        // Get all relevant model IDs including parent models
        const allRelevantModelIds = containedModelIDsInGraphs(
          expandedModelIds,
          graphs,
          modelData
        );

        // Get ranges for all models in graphs (contains model-quantity pairs)
        const rangesForModels = getRangesForModelsFromGraphs(graphs);

        // Generate model-quantity pairs for all relevant models (including parents)
        const parametersForCurves = generateModelsRangesQuantPairsForModelDist(
          allRelevantModelIds,
          rangesForModels,
          modelData
        );

        setModelsForCurvesDictionary(parametersForCurves);
      }
    } catch (error) {
      recordedErrorLog("Error in batch curve list production: ", error);
    }
  }, [listOfLoopModelsToSave, graphs, modelData]);

  // Clean up listOfLoopCurvesToSave when graphs are updated
  // Remove entries for curves that no longer exist in the graphs
  useEffect(() => {
    try {
      if (graphs === undefined) {
        return;
      }

      // Use functional update to get current state without adding to dependencies
      setListOfLoopCurvesToSave((currentList) => {
        if (!currentList || currentList.length === 0) {
          return currentList;
        }

        // Filter out entries that no longer have matching curves in graphs
        const validEntries = currentList.filter((entry) => {
          // Find the graph with matching graphId
          const graph = graphs.find((g) => g.id === entry.graphId);
          if (!graph || !graph.plotData) {
            return false;
          }

          // Check if the graph has a curve with matching modelId and quantity
          const hasMatchingCurve = graph.plotData.some((plot) => {
            return (
              hasProperty(plot, "modelId") &&
              plot.modelId === entry.modelId &&
              plot.quantity === entry.quantity
            );
          });

          return hasMatchingCurve;
        });

        // Only return new array if entries were removed, otherwise return current list
        return validEntries.length !== currentList.length
          ? validEntries
          : currentList;
      });
    } catch (error) {
      recordedErrorLog("Error cleaning up listOfLoopCurvesToSave: ", error);
    }
  }, [graphs]);

  const updateTerm = (updatedTerm) => {
    try {
    setChi2Terms((old) =>
      old.map((oldTerm) => {
        if (oldTerm.id === updatedTerm.id) {
          return updatedTerm;
        } else {
          return oldTerm;
        }
      })
    );
    } catch (error) {
      recordedErrorLog("Error updating term: ", error, true, "interface error", {updatedTerm: updatedTerm});
    }
  };

  const handleSelectParams = () => {
    setParamSelectOpen(true);
  };

  const handleSelectModels = () => {
    setAutosaveModSelectOpen(true);
  };

  const handleSelectCurves = () => {
    setExportCurvesModalOpen(true);
  };

  const generateAccumulatedVariableList = () => {
    try {
      const variableList = [];

      // Use trackedParameters array directly - much more efficient
      trackedParameters.forEach((modelSet) => {
        modelSet.selectedParams.forEach((param) => {
          let parameterEntry = {
            modelid: modelSet.FE_ID,
            speqqle_id: param.speqqle_id,
          };
          if (param.lineshape_id !== null) {
            parameterEntry.lineshape_id = param.lineshape_id;
            parameterEntry.row_index = param.rowIndex;
          } else if (param.rowIndex !== null) {
            parameterEntry.speqqle_id =
              param.speqqle_id + param.rowIndex * param.recuring;
          }
          variableList.push(parameterEntry);
        });
      });
      return variableList;
    } catch (error) {
      recordedErrorLog("Error generating accumulated variable list: ", error);
      return [];
    }
  };

  // Validation function to check if batch can be started
  const canStartBatch = () => {
    try {
      // Get filtered terms (same logic as display)
      const filteredTerms = chi2Terms.filter((term) => {
        return (
          selectedChi2Terms.includes(term.id) &&
          term.weight > 0 &&
          hasProperty(term, "activeParams") &&
          term.activeParams > 0
        );
      });

      if (filteredTerms.length === 0) {
        return false;
      }

      // Check if any terms have selectedFiles
      const termsWithSelectedFiles = filteredTerms.filter((term) => {
        return (
          hasProperty(term, "selectedLoopFiles") &&
          term.selectedLoopFiles.length > 0
        );
      });

      // If no terms have selectedFiles, batch can't start
      if (termsWithSelectedFiles.length === 0) {
        return false;
      }

      // If some terms have selectedFiles, check if the number is consistent
      if (termsWithSelectedFiles.length > 0) {
        const firstTermFileCount =
          termsWithSelectedFiles[0].selectedLoopFiles.length;

        // All terms with selectedFiles must have the same number of files
        const allHaveSameFileCount = termsWithSelectedFiles.every((term) => {
          return term.selectedLoopFiles.length === firstTermFileCount;
        });

        if (!allHaveSameFileCount) {
          return false;
        }
      }

      return true;
    } catch (error) {
      recordedErrorLog("Error validating batch start conditions: ", error);
      return false;
    }
  };

  const handleSubmit = () => {
    try {
      // let listOfModelIds = [];
      const modelsToSend = [];

      const filePayload = [];

      for (let i = 0; i < chi2Terms.length; i++) {
        const term = chi2Terms[i];

        // listOfModelIds.push(...term.modelList.map((entry) => entry.FE_ID));

        const keepCuts = hasProperty(term, "keepCuts") ? term.keepCuts : false;

        let currentTermFile = null;

        if (keepCuts) {
          currentTermFile = uploadedFiles.find(
            (file) => file.ID === term.fileId
          );
        }

        const filesToAdd =
          hasProperty(term, "selectedLoopFiles") &&
          term.selectedLoopFiles.length > 0
            ? term.selectedLoopFiles // Only use selected files if any exist
            : [{ ID: term.fileId }]; // Use original file only if no selected files

        const dataList = filesToAdd.map((selectedFile) => {
          // let relevantFile = null;

          let dataRangeMin = null;
          let dataRangeMax = null;
          let edgesToUse = [];

          const fileToProcess = uploadedFiles.find(
            (file) => file.ID === selectedFile.ID
          );

          if (
            keepCuts &&
            currentTermFile !== undefined &&
            currentTermFile !== null
          ) {
            dataRangeMin = currentTermFile.dataRangeMin;
            dataRangeMax = currentTermFile.dataRangeMax;
            edgesToUse = currentTermFile.edges;
          } else {
            dataRangeMin = fileToProcess.dataRangeMin;
            dataRangeMax = fileToProcess.dataRangeMax;
            edgesToUse = fileToProcess.edges;
          }

          let hasEdges = false;
          let filteredDataPoints = filterCurveDataToRange(
            fileToProcess.dataPoints,
            dataRangeMin,
            dataRangeMax
          );
          if (
            edgesToUse.length > 0 &&
            edgesToUse[0].min !== "" &&
            edgesToUse[0].max !== ""
          ) {
            hasEdges = true;
            filteredDataPoints = segmentsIntoSingleCurve(
              splitCurveIntoSegmentsByEdges(filteredDataPoints, edgesToUse)
            );
          }

          const details = {
            data: filteredDataPoints,
            edges: hasEdges ? edgesToUse : [],
          };
          return details;
        });

        filePayload.push({
          dataid: term.fileId,
          datalist: dataList,
        });

        modelsToSend.push(...term.modelList);
      }

      const rangesForModels = getRangesForModelsFromGraphs(graphs);

      // Process listOfLoopCurvesToSave to add to curvesPayload
      const curvesToSaveRanges = {};
      const curvesToSaveQuantityPairs = [];
      const curvesToSaveModels = [];

      if (listOfLoopCurvesToSave && listOfLoopCurvesToSave.length > 0) {
        listOfLoopCurvesToSave.forEach((curveEntry) => {
          // Get the model
          const model = getModelById(curveEntry.modelId, modelData);
          if (!model) return;

          // Add model to list if not already there
          if (!curvesToSaveModels.find((m) => m.FE_ID === model.FE_ID)) {
            curvesToSaveModels.push(model);
          }

          // Determine if unit is inverse
          const isInverse = isInverseUnit(curveEntry.unit);

          const originalXUnit = getQuantityXUnitFromModels(modelData, curveEntry.modelId, curveEntry.quantity);
          const isInverseOriginalXUnit = isInverseUnit(originalXUnit);
          // Convert range from the curve's unit to cm^-1
          const convertedRanges = convertRangeRequestFromUnitsToCm1(
            curveEntry.unit,
            [curveEntry.min, curveEntry.max],
            curveEntry.nPoints,
            originalXUnit
          );

          // Initialize ranges structure if needed
          if (!curvesToSaveRanges[curveEntry.modelId]) {
            curvesToSaveRanges[curveEntry.modelId] = {};
          }
          if (!curvesToSaveRanges[curveEntry.modelId][curveEntry.quantity]) {
            curvesToSaveRanges[curveEntry.modelId][curveEntry.quantity] = [];
          }

          // Create range entry with graphId = -1
          const rangeEntry = {
            min: convertedRanges[0].min,
            max: convertedRanges[0].max,
            nPoints: convertedRanges[0].nPoints,
            isInverseUnit: !(isInverse === isInverseOriginalXUnit),
            isLogarithmic: curveEntry.log || false,
            graphId: -1, // Always -1 for curves to save
          };

          // Handle split ranges (when crossing zero)
          if (convertedRanges.length === 2) {
            rangeEntry.min = convertedRanges[1].min;
            rangeEntry.max = convertedRanges[1].max;
            rangeEntry.nPoints = convertedRanges[1].nPoints;
            rangeEntry.minNeg = convertedRanges[0].min;
            rangeEntry.maxNeg = convertedRanges[0].max;
            rangeEntry.nPointsNeg = convertedRanges[0].nPoints;
          }

          curvesToSaveRanges[curveEntry.modelId][curveEntry.quantity].push(
            rangeEntry
          );

          // Add to quantity pairs if not already there
          if (
            !curvesToSaveQuantityPairs.find(
              (q) =>
                q.modelId === curveEntry.modelId &&
                q.quantity === curveEntry.quantity
            )
          ) {
            curvesToSaveQuantityPairs.push({
              modelId: curveEntry.modelId,
              quantity: curveEntry.quantity,
            });
          }
        });
      }

      const mergedRanges = { ...rangesForModels };
      Object.keys(curvesToSaveRanges).forEach((modelId) => {
        if (!mergedRanges[modelId]) {
          mergedRanges[modelId] = {};
        }
        Object.keys(curvesToSaveRanges[modelId]).forEach((quantity) => {
          if (!mergedRanges[modelId][quantity]) {
            mergedRanges[modelId][quantity] = [];
          }
          // Add curves to save ranges (they will have graphId = -1)
          mergedRanges[modelId][quantity].push(
            ...curvesToSaveRanges[modelId][quantity]
          );
        });
      });

      const mergedQuantityPairs = [
        ...modelsForCurvesDictionary.quantityPairs,
        ...curvesToSaveQuantityPairs.filter(
          (q) =>
            !modelsForCurvesDictionary.quantityPairs.find(
              (em) => em.modelId === q.modelId && em.quantity === q.quantity
            )
        ),
      ];

      const mergedModels = [
        ...modelsForCurvesDictionary.models,
        ...curvesToSaveModels.filter(
          (m) =>
            !modelsForCurvesDictionary.models.find((em) => em.FE_ID === m.FE_ID)
        ),
      ];

      const curvesPayload = createModelDistPayload(
        mergedModels,
        mergedRanges,
        mergedQuantityPairs,
        currentUser,
        uploadedFiles
      );

      let filesFromCurvesPayload = [];
      if (hasProperty(curvesPayload, "Data") && hasProperty(curvesPayload.Data, "SendData")) {
        filesFromCurvesPayload = curvesPayload.Data.SendData;
      }

      const fitDetails = chi2Terms
        .filter((term) => {
          // Only include terms that are:
          // 1. Selected (in selectedChi2Terms)
          // 2. Have weight > 0
          // 3. Have activeParams > 0
          return (
            selectedChi2Terms.includes(term.id) &&
            term.weight > 0 &&
            hasProperty(term, "activeParams") &&
            term.activeParams > 0
          );
        })
        .map((term) => {
          if (!hasProperty(term, "useFullRange") || term.useFullRange) {
            return {
              modelid: term.modelId,
              dataid: term.fileId,
              quantity: term.quantity,
              ranges: [{ weight: term.weight }],
            };
          } else {
            let range = { weight: term.weight };
            if (term.rangeMin !== "" && !isNaN(parseFloat(term.rangeMin))) {
              range.xmin = parseFloat(term.rangeMin);
            }
            if (term.rangeMax !== "" && !isNaN(parseFloat(term.rangeMax))) {
              range.xmax = parseFloat(term.rangeMax);
            }
            return {
              modelid: term.modelId,
              dataid: term.fileId,
              quantity: term.quantity,
              ranges: [range],
            };
          }
        });

      const dataListForModels = [];

      for (let i = 0; i < modelsToSend.length; i++) {
        const modelToSend = modelsToSend[i];
        const foundModel = getModelById(modelToSend.FE_ID, modelData);

        const paramsPayloadWithFiles = createModelFitPayloadParams([
          foundModel,
        ]);
        const paramsPayload = paramsPayloadWithFiles.sendModelData;

        const dataEntry = {
          ...paramsPayload[0],
          revert: modelToSend.option === "revert",
        };

        dataListForModels.push(dataEntry);
        const dataFilesToAdd = paramsPayloadWithFiles.addedDataFilesIds.filter(
          (fileId) => !filesFromCurvesPayload.some((file) => file.dataid === fileId)
        );
        if (dataFilesToAdd.length > 0) {
          const dataFilesToAddPayload = createDataPayloadForModels(dataFilesToAdd, uploadedFiles);
          filesFromCurvesPayload = [...filesFromCurvesPayload, ...dataFilesToAddPayload];
        }
      }

      if (filesFromCurvesPayload.length > 0) {
        if (filesFromCurvesPayload.some((file) => filePayload.some((fileInPayload) => fileInPayload.dataid === file.dataid))) {
          generateWarningObject(
            "The same data file can not be part of a model and a chi2 term at the same time.",
            2,
            setWarnings,
            setNewWarningCount
          );
          limitedToast("The same data file can not be part of a model and a chi2 term at the same time.");
          return;
        }
      }

      const fitOptions = {
        maxiters: maxIteration,
        maxsecs: maxRunTime,
        mindiffchi2: { numiters: chiVal.n, diffchi2: chiVal.val },
      };

      if (iterationRefreshCount !== "") {
        fitOptions.numupdateiters = iterationRefreshCount;
      }

      const variablesToTrack = generateAccumulatedVariableList();

      const extraModelsToSend = curvesPayload.Model.SendModel.filter(
        (model) => {
          return !dataListForModels.some(
            (modelToSend) => modelToSend.modelid === model.modelid
          );
        }
      );

      const payload = {
        User: currentUser.id,
        pause_next_iteration: true,
        Model: {
          GetModelCurve: curvesPayload.Model.GetModelCurve,
        },
        Loop: {
          FitOptions: fitOptions,
          // Fit: [{ modelid: 1, dataid: 1, weight: 1, quantity: 5 }],
          Fit: fitDetails,
          // Autofit: [{}],
          Data: filePayload,
          Model: dataListForModels,
        },
      };
      if (extraModelsToSend.length > 0) {
        payload.Model.SendModel = extraModelsToSend;
      }

      if (variablesToTrack.length > 0) {
        payload.Loop.AccumulatedOutputs = {};
        payload.Loop.AccumulatedOutputs.Variables = variablesToTrack;
      }
      if (filesFromCurvesPayload.length > 0) {
        filesFromCurvesPayload = filesFromCurvesPayload.map((file) => ({
          dataid: file.dataid,
          datalist: [{data: file.data, edges: []}],
        }));
        payload.Loop.Data = [...filePayload, ...filesFromCurvesPayload];
      }

      sendJsonMessage(payload, { type: "loop-request" });
      setPauseOnNextLoop(true);
      setIsLoopOngoing(true);
      setLoopOpen(false);
      setOutputsOpen(true);
      setParametersOpen(true);
    } catch (error) {
      recordedErrorLog(
        "Error in batch submit handling: ",
        error,
        true,
        "Failed to submit batch.",
        {
          chi2Terms: chi2Terms,
          error: error,
        }
      );
    }
  };

  const handleParamSelectClose = () => {
    setParamSelectOpen(false);
  };

  const handleModAutosaveSelectClose = () => {
    setAutosaveModSelectOpen(false);
  };

  // Handler for when curves are selected in ExportCurvesModal
  const handleExportCurves = (
    selectedCurveKeys,
    curveExportNames = {},
    sectionPrefixToggle = {},
    curveStatsState = {},
    curveLogGridState = {},
    curveUnitState = {}
  ) => {
    try {
      // Get all graphs to find the curves
      const allCurves = [];
      // When listOfLoopCurvesToSave is provided (loop mode), always include graphId in keys
      const alwaysIncludeGraphId = listOfLoopCurvesToSave !== undefined;

      graphs.forEach((graph) => {
        if (graph.plotData) {
          graph.plotData.forEach((plot) => {
            // Build the key for this curve to check if it's selected
            // Must match the format used in ExportCurvesModal
            let curveKey = "";
            if (hasProperty(plot, "modelId")) {
              if (alwaysIncludeGraphId) {
                curveKey = `model-${plot.modelId}-${plot.quantity}-${graph.id}`;
              } else {
                curveKey = `model-${plot.modelId}-${plot.quantity}`;
              }
            } else if (hasProperty(plot, "fileId")) {
              if (alwaysIncludeGraphId) {
                curveKey = `file-${plot.fileId}-${graph.id}`;
              } else {
                curveKey = `file-${plot.fileId}`;
              }
            } else if (hasProperty(plot, "variableId")) {
              if (alwaysIncludeGraphId) {
                curveKey = `variable-${plot.variableId}-${graph.id}`;
              } else {
                curveKey = `variable-${plot.variableId}`;
              }
            }

            // Only process model curves that are selected
            if (
              hasProperty(plot, "modelId") &&
              selectedCurveKeys.has(curveKey)
            ) {
              allCurves.push({
                ...plot,
                curveKey: curveKey,
                graphId: graph.id,
              });
            }
          });
        }
      });

      // Process selected model curves and create listOfLoopCurvesToSave entries
      const curvesToSave = [];

      allCurves.forEach((plot) => {
        const curveKey = plot.curveKey;

        // Get stats, unit, and log settings
        const stats = curveStatsState[curveKey];
        const unit = curveUnitState[curveKey] || units[0];
        const isLog = curveLogGridState[curveKey] || false;

        if (!stats) {
          return; // Skip if no stats available
        }

        // Get model and quantity name
        const currentModel = getModelById(plot.modelId, modelData);
        if (!currentModel) {
          return;
        }

        const quantityName =
          getQuantityNameFromModels(modelData, plot.modelId, plot.quantity) ||
          "";
        let quantityNameStr = "";
        if (quantityName) {
          if (typeof quantityName === "string") {
            quantityNameStr = quantityName;
          } else {
            // It's JSX, convert to HTML string then extract text
            quantityNameStr = jsxToHtmlString(quantityName);
            // Remove HTML tags for plain text file
            quantityNameStr = quantityNameStr.replace(/<[^>]*>/g, "");
          }
        }
        // Determine section key for this curve
        const sectionKey = "model-curves";

        // Create name using same logic as DraggableGraphWindow
        const mode = sectionPrefixToggle[sectionKey] || "prefix";
        const exportName = curveExportNames[curveKey] || "";

        // Remove .txt or .dat extension from default name if present
        let defaultName = currentModel.displayName + "_" + quantityNameStr;
        if (defaultName.endsWith(".txt")) {
          defaultName = defaultName.slice(0, -4);
        } else if (defaultName.endsWith(".dat")) {
          defaultName = defaultName.slice(0, -4);
        }
        // Remove all whitespace from defaultName
        defaultName = defaultName.replace(/\s+/g, "");

        let fileName = defaultName;
        if (mode === "export name" && exportName.trim() !== "") {
          fileName = exportName.trim();
        } else if (mode === "prefix" && exportName.trim() !== "") {
          fileName = `${exportName.trim()}${defaultName}`;
        }

        // Parse min/max/nPoints values
        const minValue =
          typeof stats.min === "string" ? parseFloat(stats.min) : stats.min;
        const maxValue =
          typeof stats.max === "string" ? parseFloat(stats.max) : stats.max;
        const nPointsValue =
          typeof stats.nPoints === "string"
            ? parseInt(stats.nPoints, 10)
            : stats.nPoints;

        // Skip if values are invalid
        if (isNaN(minValue) || isNaN(maxValue) || isNaN(nPointsValue)) {
          return;
        }

        // Create entry for listOfLoopCurvesToSave
        curvesToSave.push({
          modelId: plot.modelId,
          quantity: plot.quantity,
          graphId: plot.graphId,
          name: fileName, // Use the generated filename as the name
          min: minValue,
          max: maxValue,
          nPoints: nPointsValue,
          unit: unit,
          log: isLog,
        });
      });

      // Update listOfLoopCurvesToSave - overwrite with new selection
      setListOfLoopCurvesToSave(curvesToSave);

      // Close the modal
      setExportCurvesModalOpen(false);
    } catch (error) {
      recordedErrorLog(
        "Error handling export curves selection: ",
        error,
        true,
        "Failed to save curve selection.",
        {}
      );
    }
  };

  const handleModelSaveStatusChange = (FE_ID) => {
    setListOfLoopModelsToSave((oldList) => {
      return oldList.map((entry) => {
        if (entry.FE_ID === FE_ID) {
          return { ...entry, save: !entry.save };
        }
        return entry;
      });
    });
  };

  const updateAllModelsOnOptionChange = (modelId, option) => {
    try {
      const chi2TermsCopy = deepCopy(chi2Terms);

      for (let i = 0; i < chi2TermsCopy.length; i++) {
        const term = chi2TermsCopy[i];

        if (hasProperty(term, "modelList")) {
          chi2TermsCopy[i].modelList = chi2TermsCopy[i].modelList.map(
            (modelEntry) => {
              if (modelEntry.FE_ID === modelId) {
                return { ...modelEntry, option: option };
              }
              return modelEntry;
            }
          );
        }
      }

      setChi2Terms(chi2TermsCopy);
    } catch (error) {
      recordedErrorLog(
        "Error updating all terms models on option change: ",
        error
      );
    }
  };

  const handleSelectClicked = (
    model,
    isRec,
    param,
    rowIndex = null,
    lineshapeId = null
  ) => {
    try {
      let updatedTrackedParams = deepCopy(trackedParameters);

      // Check if parameter is already tracked
      const modelSet = updatedTrackedParams.find(
        (ms) => ms.FE_ID === model.FE_ID
      );
      const isAlreadyTracked =
        modelSet &&
        modelSet.selectedParams.some(
          (trackedParam) =>
            trackedParam.speqqle_id === param.speqqle_id &&
            trackedParam.rowIndex === rowIndex &&
            trackedParam.lineshape_id === lineshapeId
        );

      if (isAlreadyTracked) {
        // Remove parameter from tracking
        updatedTrackedParams = updatedTrackedParams.map((modelSet) => {
          if (modelSet.FE_ID === model.FE_ID) {
            return {
              ...modelSet,
              selectedParams: modelSet.selectedParams.filter(
                (trackedParam) =>
                  !(
                    trackedParam.speqqle_id === param.speqqle_id &&
                    trackedParam.rowIndex === rowIndex &&
                    trackedParam.lineshape_id === lineshapeId
                  )
              ),
            };
          }
          return modelSet;
        });
      } else {
        // Add parameter to tracking
        if (!modelSet) {
          // Create new model entry
          updatedTrackedParams.push({
            FE_ID: model.FE_ID,
            selectedParams: [
              {
                speqqle_id: param.speqqle_id,
                recuring: param.recuring,
                rowIndex: rowIndex,
                lineshape_id: lineshapeId,
              },
            ],
          });
        } else {
          // Add to existing model entry
          updatedTrackedParams = updatedTrackedParams.map((modelSet) => {
            if (modelSet.FE_ID === model.FE_ID) {
              return {
                ...modelSet,
                selectedParams: [
                  ...modelSet.selectedParams,
                  {
                    speqqle_id: param.speqqle_id,
                    recuring: param.recuring,
                    rowIndex: rowIndex,
                    lineshape_id: lineshapeId,
                  },
                ],
              };
            }
            return modelSet;
          });
        }
      }

      // Remove empty model sets
      updatedTrackedParams = updatedTrackedParams.filter(
        (modelSet) => modelSet.selectedParams.length > 0
      );

      setTrackedParameters(updatedTrackedParams);
    } catch (error) {
      recordedErrorLog(
        "Error handling select clicked: ",
        error,
        true,
        "Failed to update tracked parameters.",
        {}
      );
    }
  };

  const handleRemoveParameter = (modelId, parameterToRemove) => {
    try {
      // Remove from tracked parameters list only
      setTrackedParameters((oldTrackedParams) => {
        return oldTrackedParams
          .map((modelSet) => {
            if (modelSet.FE_ID === modelId) {
              const filteredParams = modelSet.selectedParams.filter((param) => {
                return !(
                  param.speqqle_id === parameterToRemove.speqqle_id &&
                  param.rowIndex === parameterToRemove.rowIndex &&
                  param.lineshape_id === parameterToRemove.lineshape_id
                );
              });

              return {
                ...modelSet,
                selectedParams: filteredParams,
              };
            }
            return modelSet;
          })
          .filter((modelSet) => modelSet.selectedParams.length > 0);
      });
    } catch (error) {
      recordedErrorLog(
        "Error handling remove parameter: ",
        error,
        true,
        "Failed to remove parameter from tracked parameters.",
        {}
      );
    }
  };

  return (
    <div className={`loopWindow${chi2Terms.length > 0 ? "" : " empty"}`}>
      {chi2Terms.filter((term) => {
        return (
          selectedChi2Terms.includes(term.id) &&
          term.weight > 0 &&
          hasProperty(term, "activeParams") &&
          term.activeParams > 0
        );
      }).length > 0 ? (
        <div className="contentContainer">
          <div className="loopOptions">
            <div className="loopInput">
              {chi2Terms
                .filter((term) => {
                  // Only show terms that are:
                  // 1. Selected (in selectedChi2Terms)
                  // 2. Have weight > 0
                  // 3. Have activeParams > 0
                  return (
                    selectedChi2Terms.includes(term.id) &&
                    term.weight > 0 &&
                    hasProperty(term, "activeParams") &&
                    term.activeParams > 0
                  );
                })
                .map((term, index) => (
                  <Chi2LoopEntry
                    key={`${term.id}|${index}`}
                    term={term}
                    updateTerm={updateTerm}
                    updateAllOnOptionChange={updateAllModelsOnOptionChange}
                    model={getModelById(term.modelId, modelData)}
                  />
                ))}
            </div>
            <div className="loopOutputs">
              <div className="buttonsSection">
                <div className="batchAnalysisArea">
                  {!canStartBatch() ? (
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          <div className="customTooltip">
                            All Χ<sup>2</sup> Terms should have the same number
                            of iterations.
                          </div>
                        </React.Fragment>
                      }
                    >
                      <div>
                        <CustomButton
                          text="Run Batch Analysis"
                          extraClassnames={["largeStart"]}
                          handleClick={handleSubmit}
                          disabled={true}
                        />
                      </div>
                    </HtmlTooltip>
                  ) : (
                    <CustomButton
                      text="Run Batch Analysis"
                      extraClassnames={["largeStart"]}
                      handleClick={handleSubmit}
                      disabled={false}
                    />
                  )}
                </div>
                <div className="titleContainer">
                  <div className="title">Autosaving:</div>
                </div>
                <div className="autosaveArea">
                  <HtmlTooltip
                    title={
                      !checkAccessLevel(1) ? (
                        <div className="customTooltip">
                          Please upgrade your subscription to use this feature.
                        </div>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <div>
                      <CustomButton
                        text="Select models"
                        extraClassnames={["loopExtrasVariant"]}
                        handleClick={() =>
                          checkAccessLevel(1) && handleSelectModels()
                        }
                        reference={autosaveModSelectRef}
                        disabled={!checkAccessLevel(1)}
                      />
                      <div className="selectionCount">
                        {
                          listOfLoopModelsToSave.filter(
                            (entry) => entry.save === true
                          ).length
                        }{" "}
                        models selected for export
                      </div>
                    </div>
                  </HtmlTooltip>
                  {/* <Button
                    variant="contained"
                    className="selectionButton"
                    onClick={() => handleSelectModels()}
                    style={{ backgroundColor: "#145d9d" }}
                    ref={autosaveModSelectRef}
                  >
                    Select models
                  </Button> */}
                  <HtmlTooltip
                    title={
                      !checkAccessLevel(1) ? (
                        <div className="customTooltip">
                          Please upgrade your subscription to use this feature.
                        </div>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <div>
                      <CustomButton
                        text="Select curves"
                        extraClassnames={["loopExtrasVariant"]}
                        handleClick={() =>
                          checkAccessLevel(1) && handleSelectCurves()
                        }
                        reference={autosaveCurveSelectRef}
                        disabled={!checkAccessLevel(1)}
                      />
                      <div className="selectionCount">
                        {listOfLoopCurvesToSave.length} curves selected for
                        export
                      </div>
                    </div>
                  </HtmlTooltip>
                  {/* <Button
                    variant="contained"
                    className="selectionButton"
                    onClick={() => handleSelectCurves()}
                    style={{ backgroundColor: "#145d9d" }}
                    ref={autosaveCurveSelectRef}
                  >
                    Select curves
                  </Button> */}
                </div>
                <div className="titleContainer">
                  <div className="title">Tracking:</div>
                </div>
                <div className="trackingArea">
                  <HtmlTooltip
                    title={
                      !checkAccessLevel(1) ? (
                        <div className="customTooltip">
                          Please upgrade your subscription to use this feature.
                        </div>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <div>
                      <CustomButton
                        text="Select parameters"
                        extraClassnames={["loopExtrasVariant"]}
                        handleClick={() =>
                          checkAccessLevel(1) && handleSelectParams()
                        }
                        reference={paramSelectRef}
                        disabled={!checkAccessLevel(1)}
                      />
                    </div>
                  </HtmlTooltip>
                  {/* <Button
                    variant="contained"
                    className="selectionButton"
                    onClick={() => handleSelectParams()}
                    ref={paramSelectRef}
                    style={{ backgroundColor: "#145d9d" }}
                  >
                    Select parameters
                  </Button> */}
                </div>
                {trackedParameters.length > 0 ? (
                  <div className="titleContainer">
                    <div className="title">Tracked Parameters:</div>
                  </div>
                ) : (
                  <></>
                )}
                <div className="trackedParameters">
                  {trackedParameters.length > 0 ? (
                    <TrackedParamDisplay
                      listOfTrackables={trackedParameters}
                      onRemoveParameter={handleRemoveParameter}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="noTerms">
          <div className="text">
            No Χ<sup>2</sup> Terms have been defined.
          </div>
        </div>
      )}
      <Modal
        isOpen={paramSelectOpen}
        onRequestClose={handleParamSelectClose}
        contentLabel="Confirmation Modal"
        shouldCloseOnOverlayClick={true}
        appElement={paramSelectRef.current}
        style={{
          content: {
            top: "10%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translateX(-50%)",
            width: "80vw",
            maxWidth: "1200px",
            height: "80vh",
            maxHeight: "80vh",
            padding: 0,
            borderRadius: "10px",
            border: "1px solid black",
            boxSizing: "border-box",
          },
          overlay: {
            zIndex: "999",
          },
        }}
      >
        <div className="paramSelectModal">
          <ParameterSelectorWindow
            modelList={listOfLoopModelsToSave}
            handleSelectClicked={handleSelectClicked}
            onClose={handleParamSelectClose}
          />
        </div>
      </Modal>
      <Modal
        isOpen={autosaveModSelectOpen}
        onRequestClose={handleModAutosaveSelectClose}
        contentLabel="Confirmation Modal"
        shouldCloseOnOverlayClick={true}
        appElement={autosaveModSelectRef.current}
        style={{
          content: {
            top: "35%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -35%)",
            width: "300px",
            height: "300px",
          },
          overlay: {
            zIndex: "999",
          },
        }}
      >
        <div className="modelSaveSelectModal">
          <TextField
            label="File Prefix"
            value={loopModelFilesCustomName}
            onChange={(e) => setLoopModelFilesCustomName(e.target.value)}
            className="fileNamePrefix"
            size="small"
          />
          <div className="list">
            {listOfLoopModelsToSave.map((modelEntry) => {
              return (
                <div className="modelEntry" key={modelEntry.FE_ID}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={modelEntry.save}
                          onChange={() =>
                            handleModelSaveStatusChange(modelEntry.FE_ID)
                          }
                        />
                      }
                      label={modelEntry.name}
                    />
                  </FormGroup>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
      <ExportCurvesModal
        exportModalIsOpen={exportCurvesModalOpen}
        setExportModalIsOpen={setExportCurvesModalOpen}
        reference={autosaveCurveSelectRef}
        onExport={handleExportCurves}
        graphsToInclude={graphs || []}
        zIndex={999}
        listOfLoopCurvesToSave={listOfLoopCurvesToSave}
      />
      {isFitOngoing || isLoopOngoing ? <BlockingOverlay /> : <></>}
    </div>
  );
}

export default Loop;
